import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { leadService, Lead } from '../../services/leadService';
import { actividadService } from '../../services/actividadService';

const LeadDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery(
    ['lead', id],
    () => leadService.getById(Number(id!)),
    { enabled: !!id }
  );

  const { data: actividadesData } = useQuery(
    ['actividades-lead', id],
    () => actividadService.getAll(1, 50, undefined, 'lead', Number(id!)),
    { enabled: !!id }
  );


  const lead = data?.data.lead;
  const actividades = actividadesData?.data.actividades || [];

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'nuevo':
        return 'bg-blue-100 text-blue-800';
      case 'contactado':
        return 'bg-yellow-100 text-yellow-800';
      case 'calificado':
        return 'bg-green-100 text-green-800';
      case 'descalificado':
        return 'bg-red-100 text-red-800';
      case 'convertido':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-8 text-center">
        <p className="text-gray-500">Cargando lead...</p>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="max-w-4xl mx-auto p-8 text-center">
        <p className="text-gray-500">Lead no encontrado</p>
        <button
          onClick={() => navigate('/crm/leads')}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Volver a Leads
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <button
            onClick={() => navigate('/crm/leads')}
            className="text-gray-600 hover:text-gray-800 mb-2"
          >
            ← Volver a Leads
          </button>
          <h1 className="text-3xl font-bold text-gray-800">{lead.nombre}</h1>
          <p className="text-gray-600 mt-1">
            <span className={`px-2 py-1 text-xs rounded-full ${getEstadoColor(lead.estado)}`}>
              {lead.estado}
            </span>
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => navigate(`/crm/oportunidades/nueva?leadId=${lead.id}&nombreContacto=${encodeURIComponent(lead.nombre)}&emailContacto=${encodeURIComponent(lead.email || '')}&telefonoContacto=${encodeURIComponent(lead.telefono || '')}&empresaContacto=${encodeURIComponent(lead.empresa || '')}&cargoContacto=${encodeURIComponent(lead.cargo || '')}&direccionContacto=${encodeURIComponent(lead.direccion || '')}&origen=${encodeURIComponent(lead.origen)}&valorEstimado=${lead.valorEstimado || 0}`)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Crear Oportunidad
          </button>
          <button
            onClick={() => navigate(`/crm/leads/${lead.id}/editar`)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Editar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Información principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Información de contacto */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Información del Contacto</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium">{lead.email || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Teléfono</p>
                <p className="font-medium">{lead.telefono || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Empresa</p>
                <p className="font-medium">{lead.empresa || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Cargo</p>
                <p className="font-medium">{lead.cargo || '-'}</p>
              </div>
              {lead.direccion && (
                <div className="col-span-2">
                  <p className="text-sm text-gray-600">Dirección</p>
                  <p className="font-medium">{lead.direccion}</p>
                </div>
              )}
            </div>
          </div>

          {/* Información de venta */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Información de Venta</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Valor Estimado</p>
                <p className="font-medium text-green-600">
                  ${(typeof lead.valorEstimado === 'string' ? parseFloat(lead.valorEstimado) : (lead.valorEstimado || 0)).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Probabilidad</p>
                <p className="font-medium">{lead.probabilidad}%</p>
              </div>
              {lead.fechaCierreEstimada && (
                <div>
                  <p className="text-sm text-gray-600">Fecha de Cierre Estimada</p>
                  <p className="font-medium">
                    {new Date(lead.fechaCierreEstimada).toLocaleDateString('es-MX')}
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-600">Origen</p>
                <p className="font-medium">{lead.origen}</p>
              </div>
              {lead.usuarioAsignado && (
                <div className="col-span-2">
                  <p className="text-sm text-gray-600">Asignado a</p>
                  <p className="font-medium">{lead.usuarioAsignado.name}</p>
                </div>
              )}
            </div>
          </div>

          {/* Notas */}
          {lead.notas && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Notas</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{lead.notas}</p>
            </div>
          )}

          {/* Actividades */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Actividades</h2>
              <button
                onClick={() => navigate(`/crm/actividades/nueva?relacionTipo=lead&relacionId=${lead.id}`)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                + Nueva Actividad
              </button>
            </div>
            <div className="space-y-3">
              {actividades.map((actividad) => (
                <div key={actividad.id} className="p-3 border rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900">{actividad.titulo}</p>
                      {actividad.descripcion && (
                        <p className="text-sm text-gray-500 mt-1">{actividad.descripcion}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {actividad.usuarioCreacion?.name} • {new Date(actividad.createdAt || '').toLocaleDateString('es-MX')}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      actividad.tipo === 'llamada' ? 'bg-blue-100 text-blue-800' :
                      actividad.tipo === 'visita' ? 'bg-green-100 text-green-800' :
                      actividad.tipo === 'correo' ? 'bg-indigo-100 text-indigo-800' :
                      actividad.tipo === 'whatsapp' ? 'bg-emerald-100 text-emerald-800' :
                      actividad.tipo === 'reunion' ? 'bg-purple-100 text-purple-800' :
                      actividad.tipo === 'tarea' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {actividad.tipo}
                    </span>
                  </div>
                </div>
              ))}
              {actividades.length === 0 && (
                <p className="text-gray-500 text-center py-4">No hay actividades registradas</p>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Cliente relacionado */}
          {lead.cliente && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="font-semibold text-gray-800 mb-2">Cliente Relacionado</h3>
              <p className="text-gray-900">{lead.cliente.nombre}</p>
              {lead.cliente.email && (
                <p className="text-sm text-gray-500">{lead.cliente.email}</p>
              )}
              <button
                onClick={() => navigate(`/clientes/${lead.cliente?.id}`)}
                className="mt-2 text-sm text-primary-600 hover:text-primary-700"
              >
                Ver Cliente →
              </button>
            </div>
          )}

          {/* Información adicional */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="font-semibold text-gray-800 mb-4">Información Adicional</h3>
            <div className="space-y-2 text-sm">
              <div>
                <p className="text-gray-600">Creado</p>
                <p className="font-medium">
                  {lead.createdAt ? new Date(lead.createdAt).toLocaleDateString('es-MX') : '-'}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Última actualización</p>
                <p className="font-medium">
                  {lead.updatedAt ? new Date(lead.updatedAt).toLocaleDateString('es-MX') : '-'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetail;

